import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './SuperAdminPanel.css';

const SuperAdminPanel = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newAdmin, setNewAdmin] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        education: '',
        department: '',
        phone: '',
        birthDate: '',
        facebookLink: ''
    });
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [processing, setProcessing] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Check authentication
        const token = localStorage.getItem('token');
        const userType = localStorage.getItem('userType');
        
        if (!token || userType !== 'super-admin') {
            navigate('/admin-login');
            return;
        }

        fetchAdmins();
    }, [navigate]);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch('http://localhost:8080/admin/admins', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setAdmins(data);
                setError(null);
            } else {
                setError('Failed to fetch admins');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setProcessing(true);

        try {
            const token = localStorage.getItem('token');
            
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('firstName', newAdmin.firstName);
            formData.append('lastName', newAdmin.lastName);
            formData.append('email', newAdmin.email);
            formData.append('password', newAdmin.password);
            formData.append('education', newAdmin.education);
            formData.append('department', newAdmin.department);
            formData.append('phone', newAdmin.phone || '');
            formData.append('birthDate', newAdmin.birthDate || '');
            formData.append('facebookLink', newAdmin.facebookLink || '');
            
            if (profileImage) {
                formData.append('profileImage', profileImage);
            }

            const response = await fetch('http://localhost:8080/admin/register-with-photo', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await response.json();

            if (response.ok) {
                alert('Admin created successfully!');
                setShowCreateModal(false);
                setNewAdmin({
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    education: '',
                    department: '',
                    phone: '',
                    birthDate: '',
                    facebookLink: ''
                });
                setProfileImage(null);
                setImagePreview(null);
                fetchAdmins();
            } else {
                alert(`Failed to create admin: ${data.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert('Network error: ' + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const toggleAdminStatus = async (adminId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`http://localhost:8080/admin/${adminId}/toggle-status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                alert('Admin status updated successfully!');
                fetchAdmins();
            } else {
                alert('Failed to update admin status');
            }
        } catch (err) {
            alert('Network error: ' + err.message);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        localStorage.removeItem('userInfo');
        navigate('/admin-login');
    };

    if (loading) {
        return (
            <div className="super-admin-panel">
                <Header />
                <div className="loading-container">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                    <p>Loading admins...</p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="super-admin-panel">
            <Header />
            <div className="admin-header">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <h1>Super Admin Panel</h1>
                            <p>Manage administrators and system access</p>
                            <button className="btn btn-danger logout-btn" onClick={handleLogout}>
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container" style={{ padding: '0 0 50px 0' }}>
                {error && (
                    <div className="alert alert-danger error-alert" role="alert">
                        {error}
                        <button 
                            className="btn btn-sm btn-outline-danger ml-2" 
                            onClick={fetchAdmins}
                        >
                            Retry
                        </button>
                    </div>
                )}

                <div className="admin-actions">
                    <button 
                        className="btn btn-primary create-admin-btn"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <i className="fa fa-plus mr-2"></i>
                        Create New Admin
                    </button>
                </div>

                <div className="admins-table">
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="thead-dark">
                                <tr>
                                    <th>Photo</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Education</th>
                                    <th>Department</th>
                                    <th>Status</th>
                                    <th>Created By</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="no-admins">
                                            <i className="fa fa-users"></i>
                                            <div>No admins found</div>
                                        </td>
                                    </tr>
                                ) : (
                                    admins.map((admin) => (
                                        <tr key={admin.id}>
                                            <td>
                                                <div className="admin-photo">
                                                    {admin.profileImage ? (
                                                        <img 
                                                            src={`http://localhost:8080/uploads/${admin.profileImage}`}
                                                            alt={`${admin.firstName} ${admin.lastName}`}
                                                            className="profile-image"
                                                        />
                                                    ) : (
                                                        <div className="default-avatar">
                                                            <i className="fa fa-user"></i>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <strong>{admin.firstName} {admin.lastName}</strong>
                                            </td>
                                            <td>{admin.email}</td>
                                            <td>{admin.education}</td>
                                            <td>{admin.department}</td>
                                            <td>
                                                <span className={`badge ${admin.isActive ? 'badge-success' : 'badge-danger'}`}>
                                                    {admin.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{admin.createdBy || 'System'}</td>
                                            <td>
                                                <button 
                                                    className={`btn btn-sm ${admin.isActive ? 'btn-warning' : 'btn-success'}`}
                                                    onClick={() => toggleAdminStatus(admin.id)}
                                                >
                                                    {admin.isActive ? 'Deactivate' : 'Activate'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Create Admin Modal */}
                {showCreateModal && (
                    <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Create New Admin</h5>
                                    <button 
                                        type="button" 
                                        className="close" 
                                        onClick={() => setShowCreateModal(false)}
                                    >
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <form onSubmit={handleCreateAdmin}>
                                    <div className="modal-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="firstName">First Name *</label>
                                                    <input
                                                        type="text"
                                                        id="firstName"
                                                        className="form-control"
                                                        value={newAdmin.firstName}
                                                        onChange={(e) => setNewAdmin({...newAdmin, firstName: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="lastName">Last Name *</label>
                                                    <input
                                                        type="text"
                                                        id="lastName"
                                                        className="form-control"
                                                        value={newAdmin.lastName}
                                                        onChange={(e) => setNewAdmin({...newAdmin, lastName: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="email">Email *</label>
                                                    <input
                                                        type="email"
                                                        id="email"
                                                        className="form-control"
                                                        value={newAdmin.email}
                                                        onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="password">Password *</label>
                                                    <input
                                                        type="password"
                                                        id="password"
                                                        className="form-control"
                                                        value={newAdmin.password}
                                                        onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                                                        required
                                                        minLength="6"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="education">Education *</label>
                                                    <input
                                                        type="text"
                                                        id="education"
                                                        className="form-control"
                                                        value={newAdmin.education}
                                                        onChange={(e) => setNewAdmin({...newAdmin, education: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="department">Department *</label>
                                                    <input
                                                        type="text"
                                                        id="department"
                                                        className="form-control"
                                                        value={newAdmin.department}
                                                        onChange={(e) => setNewAdmin({...newAdmin, department: e.target.value})}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="phone">Phone</label>
                                                    <input
                                                        type="tel"
                                                        id="phone"
                                                        className="form-control"
                                                        value={newAdmin.phone}
                                                        onChange={(e) => setNewAdmin({...newAdmin, phone: e.target.value})}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="birthDate">Birth Date</label>
                                                    <input
                                                        type="date"
                                                        id="birthDate"
                                                        className="form-control"
                                                        value={newAdmin.birthDate}
                                                        onChange={(e) => setNewAdmin({...newAdmin, birthDate: e.target.value})}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="facebookLink">Facebook Link</label>
                                                    <input
                                                        type="url"
                                                        id="facebookLink"
                                                        className="form-control"
                                                        value={newAdmin.facebookLink}
                                                        onChange={(e) => setNewAdmin({...newAdmin, facebookLink: e.target.value})}
                                                        placeholder="https://facebook.com/username"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label htmlFor="profileImage">Profile Photo</label>
                                                    <input
                                                        type="file"
                                                        id="profileImage"
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                    />
                                                    {imagePreview && (
                                                        <div className="image-preview">
                                                            <img 
                                                                src={imagePreview} 
                                                                alt="Preview" 
                                                                className="preview-image"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary" 
                                            onClick={() => setShowCreateModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="submit" 
                                            className="btn btn-primary"
                                            disabled={processing}
                                        >
                                            {processing ? 'Creating...' : 'Create Admin'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};

export default SuperAdminPanel;
